/**
 * AvatarGuide — public click-through onboarding for anyone coming to
 * record a Synthesia avatar with NicheSocial. Talent reads this once
 * before the shoot day; agencies share the link with whoever they're
 * sending in front of the camera.
 *
 * Pure CSS animations, no external deps beyond lucide-react.
 */
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Sparkles,
  FileText,
  Shirt,
  Gem,
  Brush,
  Coffee,
  Clock,
  Eye,
  AudioLines,
  Heart,
  ChevronLeft,
  ChevronRight,
  Camera,
} from "lucide-react";
import "./AvatarGuide.css";

const STEPS = [
  { key: "welcome",     title: "Welcome — you're going to look great", Icon: Sparkles },
  { key: "script",      title: "Read the script before you arrive",     Icon: FileText },
  { key: "wardrobe",    title: "Dress to be seen — not blended out",    Icon: Shirt },
  { key: "accessories", title: "Keep accessories quiet and small",      Icon: Gem },
  { key: "hairmakeup",  title: "Hair off the face, makeup matte",       Icon: Brush },
  { key: "fuel",        title: "Eat light. Drink water. Skip the milk.",Icon: Coffee },
  { key: "day",         title: "What the shoot day looks like",         Icon: Clock },
  { key: "lens",        title: "Look down the lens, not at us",         Icon: Eye },
  { key: "pace",        title: "Slow down. Pause. Breathe.",            Icon: AudioLines },
  { key: "relax",       title: "Relax. The best takes feel easy.",      Icon: Heart },
];

export default function AvatarGuide({ onBack }) {
  const [active, setActive] = useState(0);
  const total = STEPS.length;
  const step = STEPS[active];

  // Keyboard nav so it feels like a proper deck
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") setActive((i) => Math.min(i + 1, total - 1));
      if (e.key === "ArrowLeft")  setActive((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [total]);

  const go = (n) => setActive(Math.min(Math.max(n, 0), total - 1));

  return (
    <div className="ag-page" data-testid="avatar-guide">
      {/* Top bar */}
      <header className="ag-nav">
        {onBack ? (
          <button className="ag-back" onClick={onBack} data-testid="ag-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
        ) : <span />}
        <span className="ag-wordmark">
          <span className="ag-wordmark-niche">niche</span>
          <span className="ag-wordmark-social">social</span>
          <span className="ag-wordmark-divider">·</span>
          <span className="ag-wordmark-sub">Talent guide</span>
        </span>
        <span className="ag-step-count" data-testid="ag-step-count">
          {String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </header>

      {/* Progress bar */}
      <div className="ag-progress" aria-hidden="true">
        <span style={{ width: `${((active + 1) / total) * 100}%` }} />
      </div>

      {/* Stage */}
      <main className="ag-stage" data-testid={`ag-slide-${step.key}`}>
        <button
          type="button"
          className="ag-arrow ag-arrow-prev"
          onClick={() => go(active - 1)}
          disabled={active === 0}
          aria-label="Previous"
          data-testid="ag-prev-btn"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          className="ag-arrow ag-arrow-next"
          onClick={() => go(active + 1)}
          disabled={active === total - 1}
          aria-label="Next"
          data-testid="ag-next-btn"
        >
          <ChevronRight size={20} />
        </button>

        <div className="ag-slide" key={step.key}>
          <div className="ag-slide-grid">
            <div className="ag-slide-copy">
              <div className="ag-step-pill">
                <step.Icon size={13} /> Step {active + 1}
              </div>
              <h1 className="ag-slide-title">{step.title}</h1>
              <SlideBody slide={step.key} />
            </div>

            <div className="ag-slide-visual">
              {step.key === "welcome"     && <VisualWelcome />}
              {step.key === "script"      && <VisualScript />}
              {step.key === "wardrobe"    && <VisualWardrobe />}
              {step.key === "accessories" && <VisualAccessories />}
              {step.key === "hairmakeup"  && <VisualHairMakeup />}
              {step.key === "fuel"        && <VisualFuel />}
              {step.key === "day"         && <VisualDay />}
              {step.key === "lens"        && <VisualLens />}
              {step.key === "pace"        && <VisualPace />}
              {step.key === "relax"       && <VisualRelax />}
            </div>
          </div>
        </div>
      </main>

      {/* Dots + bottom controls */}
      <footer className="ag-footer">
        <div className="ag-dots" role="tablist" aria-label="Guide steps">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={i === active}
              aria-label={s.title}
              className={`ag-dot ${i === active ? "is-active" : ""} ${i < active ? "is-done" : ""}`}
              onClick={() => go(i)}
              data-testid={`ag-dot-${s.key}`}
            />
          ))}
        </div>
        <div className="ag-footer-cta">
          {active < total - 1 ? (
            <button className="ag-next-cta" onClick={() => go(active + 1)} data-testid="ag-next-cta">
              Next: {STEPS[active + 1].title.split(" — ")[0]} <ArrowRight size={14} />
            </button>
          ) : (
            <button className="ag-next-cta ag-next-cta-finish" onClick={() => go(0)} data-testid="ag-restart-btn">
              Start over <ArrowRight size={14} />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   COPY for each slide
   ───────────────────────────────────────────────────────────── */
function SlideBody({ slide }) {
  switch (slide) {
    case "welcome":
      return (
        <>
          <p className="ag-lede">
            You're recording an AI avatar with us. It's quicker, calmer and a lot
            more fun than people expect — we promise.
          </p>
          <p className="ag-body">
            This guide is a 2-minute scroll. Read it once before you come in and
            you'll be miles ahead of every first-timer who hasn't.
          </p>
          <ul className="ag-list">
            <li><CheckCircle2 size={14} /> No experience needed — we coach you on the day</li>
            <li><CheckCircle2 size={14} /> No memorising scripts</li>
            <li><CheckCircle2 size={14} /> You'll be done in under 90 minutes</li>
          </ul>
        </>
      );
    case "script":
      return (
        <>
          <p className="ag-lede">We've sent you the script. Have a look before you arrive.</p>
          <p className="ag-body">
            Don't memorise it — that's not what we're after. Read it out loud
            once or twice so the words feel like yours, not someone else's.
          </p>
          <ul className="ag-list">
            <li><CheckCircle2 size={14} /> Skim for any words that feel off — flag them so we can swap them</li>
            <li><CheckCircle2 size={14} /> Note the natural pauses. Commas matter.</li>
            <li><CheckCircle2 size={14} /> You'll see the script live in the room. No autocue panic.</li>
          </ul>
        </>
      );
    case "wardrobe":
      return (
        <>
          <p className="ag-lede">
            The camera loves mid-tones. It hates head-to-toe white and
            anything with a logo on it.
          </p>
          <div className="ag-do-dont">
            <div className="ag-do">
              <div className="ag-do-head"><CheckCircle2 size={14} /> Do</div>
              <ul>
                <li>Solid mid-tone colours — soft greens, navies, charcoals, burgundies</li>
                <li>A crisp shirt <em>under</em> a jacket or blazer is welcome</li>
                <li>Tailored fits — they read sharper on camera</li>
                <li>Matte fabrics over satin or sheen</li>
              </ul>
            </div>
            <div className="ag-dont">
              <div className="ag-do-head ag-dont-head"><XCircle size={14} /> Don't</div>
              <ul>
                <li>All-white outfits — the camera blows them out</li>
                <li>Visible logos, slogans or branded patterns</li>
                <li>Tight stripes, herringbone or busy prints (they shimmer)</li>
                <li>Pure black head-to-toe — it eats your shape</li>
              </ul>
            </div>
          </div>
        </>
      );
    case "accessories":
      return (
        <>
          <p className="ag-lede">Less is more. Quiet is everything.</p>
          <p className="ag-body">
            Anything that catches light or clinks together will get picked up
            by the mic. Save the statement pieces for after the shoot.
          </p>
          <ul className="ag-list">
            <li><CheckCircle2 size={14} /> Small studs over hoops or chandeliers</li>
            <li><CheckCircle2 size={14} /> A single watch or one bracelet — not both wrists</li>
            <li><XCircle size={14} className="ag-x" /> No bangles that jingle</li>
            <li><XCircle size={14} className="ag-x" /> No reflective sunglasses on your head</li>
          </ul>
        </>
      );
    case "hairmakeup":
      return (
        <>
          <p className="ag-lede">Whatever you'd do for a good headshot. Then take it down 10%.</p>
          <p className="ag-body">
            We're not doing a fashion shoot — we want you to look like the best
            version of yourself on a good day.
          </p>
          <ul className="ag-list">
            <li><CheckCircle2 size={14} /> Hair off the face, freshly washed</li>
            <li><CheckCircle2 size={14} /> Matte over dewy — studio lights find every shine</li>
            <li><CheckCircle2 size={14} /> A light dusting of powder, even if you never wear any</li>
            <li><CheckCircle2 size={14} /> Concealer for under-eyes if you've had a late one</li>
          </ul>
        </>
      );
    case "fuel":
      return (
        <>
          <p className="ag-lede">A light meal an hour before. Plenty of water.</p>
          <p className="ag-body">
            Hungry on camera looks distracted. Stuffed on camera looks sluggish.
            Find the in-between.
          </p>
          <ul className="ag-list">
            <li><CheckCircle2 size={14} /> Water within arm's reach throughout</li>
            <li><CheckCircle2 size={14} /> Black coffee or tea is fine — just not five of them</li>
            <li><XCircle size={14} className="ag-x" /> Skip dairy in the hour before (thickens your voice)</li>
            <li><XCircle size={14} className="ag-x" /> Avoid fizzy drinks — they cause burps mid-take</li>
          </ul>
        </>
      );
    case "day":
      return (
        <>
          <p className="ag-lede">Door-to-done in around 90 minutes. Here's the running order.</p>
          <ol className="ag-timeline">
            <li><span className="ag-t-time">0:00</span><span className="ag-t-step">Arrive, kettle on, settle in</span></li>
            <li><span className="ag-t-time">0:10</span><span className="ag-t-step">Mic up, framing check</span></li>
            <li><span className="ag-t-time">0:20</span><span className="ag-t-step">Warm-up read — feels like a rehearsal</span></li>
            <li><span className="ag-t-time">0:35</span><span className="ag-t-step">Filming in short, friendly takes</span></li>
            <li><span className="ag-t-time">1:20</span><span className="ag-t-step">Quick check-back, you're done</span></li>
          </ol>
          <p className="ag-body ag-body-faded">
            The first take is always a warm-up. We don't expect it to make the
            cut, so don't sweat it.
          </p>
        </>
      );
    case "lens":
      return (
        <>
          <p className="ag-lede">Treat the camera lens like an old friend you're catching up with.</p>
          <p className="ag-body">
            Don't look at us. Don't look at the script. We'll mark the spot for
            you. Hold the eyeline steady — that's the single biggest thing that
            makes avatar footage feel real.
          </p>
          <ul className="ag-list">
            <li><CheckCircle2 size={14} /> Eyeline on the dot above the lens</li>
            <li><CheckCircle2 size={14} /> Lift your chin a fraction — don't drop it</li>
            <li><CheckCircle2 size={14} /> Blink naturally. Don't try not to.</li>
          </ul>
        </>
      );
    case "pace":
      return (
        <>
          <p className="ag-lede">20% slower than feels natural. Pauses are powerful.</p>
          <p className="ag-body">
            On camera, every "um" feels louder, every rush feels faster, every
            pause feels shorter. Slow it right down — the playback will sound
            normal, we promise.
          </p>
          <ul className="ag-list">
            <li><CheckCircle2 size={14} /> Land the end of each sentence — don't trail off</li>
            <li><CheckCircle2 size={14} /> Breathe between thoughts, not mid-sentence</li>
            <li><CheckCircle2 size={14} /> If you fluff a line, just restart the sentence — we'll cut it</li>
          </ul>
        </>
      );
    case "relax":
      return (
        <>
          <p className="ag-lede">The takes that feel "too easy" are the ones that always make the cut.</p>
          <p className="ag-body">
            Every single person we've ever filmed says afterwards "actually
            that was way more fun than I thought." It will be.
          </p>
          <p className="ag-body">
            See you on the day — and bring your good mood. We'll bring the
            coffee.
          </p>
        </>
      );
    default:
      return null;
  }
}

/* ─────────────────────────────────────────────────────────────
   VISUALS — pure CSS keyframes
   ───────────────────────────────────────────────────────────── */
function VisualWelcome() {
  return (
    <div className="ag-vis ag-vis-welcome">
      <div className="ag-spotlight" />
      <div className="ag-silhouette" />
      <div className="ag-vis-camera"><Camera size={26} /></div>
      <div className="ag-vis-sparkle ag-vis-sparkle-1"><Sparkles size={14} /></div>
      <div className="ag-vis-sparkle ag-vis-sparkle-2"><Sparkles size={12} /></div>
      <div className="ag-vis-sparkle ag-vis-sparkle-3"><Sparkles size={10} /></div>
    </div>
  );
}

function VisualScript() {
  return (
    <div className="ag-vis ag-vis-script">
      <div className="ag-paper">
        <div className="ag-paper-line ag-paper-line-1" />
        <div className="ag-paper-line ag-paper-line-2" />
        <div className="ag-paper-line ag-paper-line-3" />
        <div className="ag-paper-line ag-paper-line-4" />
        <div className="ag-paper-line ag-paper-line-5" />
        <div className="ag-paper-highlight" />
      </div>
    </div>
  );
}

function VisualWardrobe() {
  return (
    <div className="ag-vis ag-vis-wardrobe">
      <div className="ag-mannequin ag-mannequin-do">
        <span className="ag-mannequin-flag ag-flag-do"><CheckCircle2 size={11} /> Do</span>
        <div className="ag-head" />
        <div className="ag-torso ag-torso-do">
          <div className="ag-jacket ag-jacket-do" />
          <div className="ag-shirt-collar" />
        </div>
      </div>
      <div className="ag-mannequin ag-mannequin-dont">
        <span className="ag-mannequin-flag ag-flag-dont"><XCircle size={11} /> Don't</span>
        <div className="ag-head" />
        <div className="ag-torso ag-torso-dont">
          <div className="ag-logo-badge">LOGO</div>
        </div>
      </div>
    </div>
  );
}

function VisualAccessories() {
  return (
    <div className="ag-vis ag-vis-acc">
      <div className="ag-acc-ring ag-acc-ring-1" />
      <div className="ag-acc-ring ag-acc-ring-2" />
      <div className="ag-acc-ring ag-acc-ring-3" />
      <div className="ag-acc-bangle ag-acc-bangle-1" />
      <div className="ag-acc-bangle ag-acc-bangle-2" />
      <div className="ag-acc-cross"><XCircle size={48} /></div>
    </div>
  );
}

function VisualHairMakeup() {
  return (
    <div className="ag-vis ag-vis-hair">
      <div className="ag-face">
        <div className="ag-face-hair" />
        <div className="ag-face-eye ag-face-eye-l" />
        <div className="ag-face-eye ag-face-eye-r" />
        <div className="ag-face-blush ag-face-blush-l" />
        <div className="ag-face-blush ag-face-blush-r" />
        <div className="ag-face-lips" />
      </div>
      <div className="ag-brush" />
    </div>
  );
}

function VisualFuel() {
  return (
    <div className="ag-vis ag-vis-fuel">
      <div className="ag-glass">
        <div className="ag-glass-water" />
        <div className="ag-glass-bubble ag-glass-bubble-1" />
        <div className="ag-glass-bubble ag-glass-bubble-2" />
        <div className="ag-glass-bubble ag-glass-bubble-3" />
      </div>
      <div className="ag-apple">
        <div className="ag-apple-leaf" />
        <div className="ag-apple-stem" />
      </div>
    </div>
  );
}

function VisualDay() {
  const dots = ["0:00", "0:10", "0:20", "0:35", "1:20"];
  return (
    <div className="ag-vis ag-vis-day">
      <div className="ag-track">
        <div className="ag-track-fill" />
        {dots.map((t, i) => (
          <div key={t} className="ag-track-dot" style={{ left: `${(i / (dots.length - 1)) * 100}%`, animationDelay: `${i * 220}ms` }}>
            <span>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VisualLens() {
  return (
    <div className="ag-vis ag-vis-lens">
      <div className="ag-lens-ring ag-lens-ring-1" />
      <div className="ag-lens-ring ag-lens-ring-2" />
      <div className="ag-lens-ring ag-lens-ring-3" />
      <div className="ag-lens-iris">
        <div className="ag-lens-glint" />
      </div>
      <div className="ag-lens-spot" />
    </div>
  );
}

function VisualPace() {
  return (
    <div className="ag-vis ag-vis-pace">
      <div className="ag-wave">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} style={{ animationDelay: `${i * 80}ms` }} />
        ))}
      </div>
      <div className="ag-wave-pause">
        <span /><span /><span />
      </div>
    </div>
  );
}

function VisualRelax() {
  return (
    <div className="ag-vis ag-vis-relax">
      <div className="ag-heart"><Heart size={56} fill="#f6b545" strokeWidth={0} /></div>
      <div className="ag-relax-glow" />
      <div className="ag-relax-sparkle ag-relax-sparkle-1"><Sparkles size={14} /></div>
      <div className="ag-relax-sparkle ag-relax-sparkle-2"><Sparkles size={12} /></div>
      <div className="ag-relax-sparkle ag-relax-sparkle-3"><Sparkles size={10} /></div>
      <div className="ag-relax-sparkle ag-relax-sparkle-4"><Sparkles size={11} /></div>
    </div>
  );
}
